import React from 'react';
import useLogPage from '../useLogPage';
import LogFilters from '../LogFilters';
import LogTable from '../LogTable';
import LogStatCards from '../LogStatCards';
import { TrendChart, GroupedBarChart } from '../LogCharts';
import { LogService } from '../../../services/activityLogService';

// Matches actual activity_type values stored by insert_activity_log()
const ACTION_OPTIONS = [
  { value: 'ADD_TO_CART',      label: 'Add to Cart'      },
  { value: 'REMOVE_FROM_CART', label: 'Remove from Cart' },
  { value: 'PRODUCT_VIEWED',   label: 'Product Viewed'   },
  { value: 'WISHLIST_ADD',     label: 'Wishlist Add'     },
  { value: 'WISHLIST_REMOVE',  label: 'Wishlist Remove'  },
  { value: 'SEARCH',           label: 'Search'           },
];

const actionBadge = (v) => {
  const map = {
    ADD_TO_CART:      ['#D1FAE5', '#065F46'],
    REMOVE_FROM_CART: ['#FEE2E2', '#991B1B'],
    PRODUCT_VIEWED:   ['#DBEAFE', '#1E40AF'],
    WISHLIST_ADD:     ['#FEF3C7', '#92400E'],
    WISHLIST_REMOVE:  ['#F3F4F6', '#374151'],
    SEARCH:           ['#E0F2FE', '#0369A1'],
  };
  const [bg, color] = map[v?.toUpperCase()] || ['#F3F4F6', '#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const COLUMNS = [
  { key: 'id',           label: '#',          render: (v) => <span className="al-mono">{v}</span> },
  { key: 'user_id',      label: 'User ID',    render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'username',     label: 'User',       render: (v) => v || '—' },
  { key: 'action',       label: 'Action',     render: actionBadge },
  { key: 'module',       label: 'Module' },
  { key: 'business_name',label: 'Business',   render: (v) => v || '—' },
  { key: 'description',  label: 'Details',    render: (v) => <span className="al-truncate" title={v}>{v || '—'}</span> },
  { key: 'ip_address',   label: 'IP',         render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'created_at',   label: 'Time',       render: (v) => v ? new Date(v).toLocaleString('en-IN') : '—' },
];

const UserActivityLog = () => {
  const lp = useLogPage(LogService.userActivity);
  const s = lp.stats || {};

  const handleExport = () => {
    const { search, status, dateFrom, dateTo } = lp.filters;
    LogService.exportUserActivity({
      ...(search   ? { search }              : {}),
      ...(status   ? { status }              : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo   ? { date_to:   dateTo   } : {}),
      format: 'excel',
    });
  };

  const stats = [
    { label: 'Total Events',    value: lp.totalItems.toLocaleString(),              color: '#10B981' },
    { label: 'Unique Users',    value: (s.unique_users   ?? 0).toLocaleString(),    color: '#3B82F6' },
    { label: 'Actions Today',   value: (s.actions_today  ?? 0).toLocaleString(),    color: '#F55D00' },
    { label: 'Active Modules',  value: (s.active_modules ?? 0).toLocaleString(),    color: '#8B5CF6' },
  ];

  const barData = lp.donut.length
    ? lp.donut
    : Object.entries(s.breakdown || {}).map(([action, count]) => ({ action, count }));

  return (
    <div className="al-page">
      <LogStatCards stats={stats} />

      <div className="al-charts">
        <TrendChart title="User Activity Over Time" data={lp.trend} color="#10B981" />
        <GroupedBarChart
          title="Actions by Type"
          data={barData}
          bars={[{ key: 'value', label: 'Events' }]}
          xKey="name"
        />
      </div>

      <LogFilters
        filters={lp.filters}
        onFilterChange={lp.handleFilterChange}
        onApply={lp.handleApply}
        onReset={lp.handleReset}
        statusOptions={ACTION_OPTIONS}
        loading={lp.loading}
      />

      <LogTable
        title="User Activity Logs"
        columns={COLUMNS}
        rows={lp.rows}
        loading={lp.loading}
        error={lp.error}
        page={lp.page}
        totalPages={lp.totalPages}
        totalItems={lp.totalItems}
        pageSize={lp.filters.pageSize}
        onPageChange={lp.handlePageChange}
        onExport={handleExport}
        onRefresh={lp.handleRefresh}
      />
    </div>
  );
};

export default UserActivityLog;
